// Pure helpers for the Home screen's POP tag scan button (Session APG). "POP" is Proof Of Presence.
// DOM-free, so the wording and the cache rule unit-test directly in node, like popTag.ts beside it.
//
// WHAT THE BUTTON DOES, for the reader who has not opened the rest. The engineer presses it on Home,
// holds the phone to the tag, and the platform (which already knows the lift from the tag) finds their
// own open service visit or call-out at that lift (booked for today, overdue or undated) and logs the tap as its arrival or its departure, whichever that job
// is waiting for. No open job: the tap is still recorded against the lift and the engineer is told
// plainly it is linked to nothing. Several open jobs: the engineer says which one. The tap itself is
// written by the same route the job sheet's own tag step uses, so there is one record and never two.
//
// THIS FILE OWNS TWO THINGS: what each outcome is called on screen, and how a tap that has landed is
// folded into the cached job. It owns no rule about WHICH job or WHICH end: that is the server's
// (server/src/modules/engineer/popTagMatch.ts), because the end a job is waiting for is read from taps
// only the server holds.
import type { PopTagCandidate, PopTagNoMatchReason, PublicTapResult } from "./api";
import { jobTypeLabel } from "./jobType";
import { POP_TAG_OUTCOME_COPY, type PopTagOutcome } from "./popTag";

export type PopTagEnd = "arrival" | "departure";

// The two ends in the engineer's own words. The job sheet's tag steps say "arriving" and "leaving"
// (PopTagStep.tsx) and its confirmation reads "Arrival recorded" and "Leaving recorded", so the Home
// button says "Arrival logged" and "Leaving logged" and an engineer sees one vocabulary across both.
export function endNoun(end: PopTagEnd): string {
  return end === "arrival" ? "Arrival" : "Leaving";
}

// When a job in the picker is booked for, in the engineer's own words: "Booked for 1 Oct, 20 days overdue"
// for an overdue visit, and nothing for today's or an undated one. The date is the London calendar day.
export function bookedLine(c: Pick<PopTagCandidate, "scheduledFor" | "daysOverdue">): string | null {
  const days = c.daysOverdue ?? 0;
  if (!c.scheduledFor || days <= 0) return null;
  const when = new Date(c.scheduledFor).toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "Europe/London" });
  return `Booked for ${when}, ${days} ${days === 1 ? "day" : "days"} overdue`;
}

// What a button that will log this end promises before it is pressed, in the picker.
export function endPromise(end: PopTagEnd): string {
  return end === "arrival" ? "Logs your arrival" : "Logs your leaving";
}

// One job, as the picker names it: its plain-English type and, when it has one, its reference, which
// is what tells two jobs of the same type apart ("Call-out · CO-ELE-085-21-09-26").
export function candidateLabel(c: Pick<PopTagCandidate, "type" | "jobNumber">): string {
  return c.jobNumber ? `${jobTypeLabel(c.type)} · ${c.jobNumber}` : jobTypeLabel(c.type);
}

// A scan the phone must ASK about before it logs it: a departure that would land inside the platform's
// three-minute repeat window of the job's own arrival. The end a scan is comes from the job's own taps, so
// a second press soon after an arrival reads as the leaving. That is right for a real visit and wrong for
// the two ordinary accidents the window exists to absorb (a press whose answer was lost with the signal
// after the server had written the row, and a second press with the phone still on the tag), either of
// which would log a leaving seconds after the arrival: a fabricated visit of no length. The rule (an
// arrival if none, otherwise a departure) is unchanged; only a one-tap confirmation is added, and only in
// this narrow window, so an engineer who really is leaving has one extra tap and nobody else is asked.
// `=== true`, never truthiness: absent (an older server) reads as no question, the pre-review behaviour.
export function needsLeaveConfirm(c: Pick<PopTagCandidate, "nextEnd" | "arrivedRecently">): boolean {
  return c.nextEnd === "departure" && c.arrivedRecently === true;
}

export interface LeaveQuestion {
  heading: string;
  // The job the question is about, named as the picker names it.
  job: string;
  body: string;
  yes: string;
  no: string;
}

// The words of that question. `arrivedTime` is the arrival's London wall clock, or null when the server did
// not say (the sentence then drops the time rather than inventing one).
export function leaveQuestion(input: {
  siteName: string;
  assetRef: string;
  job: Pick<PopTagCandidate, "type" | "jobNumber">;
  arrivedTime: string | null;
}): LeaveQuestion {
  const where = input.siteName || input.assetRef;
  const logged = input.arrivedTime
    ? `You logged your arrival at ${input.arrivedTime}, less than three minutes ago.`
    : "You logged your arrival less than three minutes ago.";
  return {
    heading: "Leaving already?",
    job: candidateLabel(input.job),
    body: `${logged} Do you want to log your leaving from ${where} now?`,
    yes: "Yes, log my leaving",
    no: "No, keep my arrival",
  };
}

// The job types the tag does not match, in the engineer's own words with their article. Anything else
// (a type added later) falls back to a plain phrase rather than a raw key.
const OTHER_TYPE_PHRASE: Record<string, string> = {
  repair: "a repair",
  tech_visit: "a technician visit",
  loler_follow_up: "a LOLER follow-up",
  project_works: "a project works job",
  project_survey: "a project survey",
  condition_report: "a condition report",
  site_inspection: "a site inspection",
  loler: "a LOLER examination",
};

export function otherTypePhrase(type: string | null | undefined): string {
  return (type ? OTHER_TYPE_PHRASE[type] : undefined) ?? "a job of another type";
}

export type HomeScanTone = "ok" | "note" | "warn";

export interface HomeScanSummary {
  tone: HomeScanTone;
  heading: string;
  // Each rendered as its own line. Never empty.
  lines: string[];
}

export interface HomeScanSummaryInput {
  assetRef: string;
  siteName: string;
  // The job the tap was made for, or null when there was none to match. ONLY the job as the phone
  // chose it: what the server says was recorded is `result`, and the two are compared, not trusted.
  job: Pick<PopTagCandidate, "type" | "jobNumber" | "nextEnd"> | null;
  result: PublicTapResult;
  outcome: PopTagOutcome;
  // The recorded time, already formatted as London wall clock (londonTime in usePopTagScan.ts).
  time: string;
  // Why the server found no job to match, when it found none (see PopTagNoMatchReason). Read only in the
  // no-job branch, so that "no open job here today" is not said of a job the engineer has just finished
  // or of an account that has no jobs of its own.
  noJobReason?: PopTagNoMatchReason | null;
  // With noJobReason "other_type": the JobType of the open job the tag does not match.
  otherType?: string | null;
}

// The words for what just happened. Composed here rather than in the JSX so they can be pinned by a
// test: two of them exist to stop a quiet over-claim ("Arrival logged" for a tap the platform did NOT
// log as one), and an over-claim is a wording defect.
//
// THE ORDER OF THE BRANCHES IS THE LOGIC. An install confirmation is decided first and on its own,
// because it is the one outcome where "logged" would be false whatever else is true: the first scan of
// a newly fitted sticker records no attendance for anybody, in either direction, and says so. Then the
// outcomes where the server could not name the engineer, which must never read as a clean log.
export function homeScanSummary(input: HomeScanSummaryInput): HomeScanSummary {
  const { assetRef, siteName, job, result, outcome, time } = input;
  const where = siteName || assetRef;
  const copy = POP_TAG_OUTCOME_COPY[outcome];

  if (outcome === "install_confirmed") {
    return {
      tone: "note",
      heading: copy.heading,
      lines: [
        copy.body,
        ...(job ? [`Scan the tag again to log your ${job.nextEnd === "arrival" ? "arrival" : "leaving"}, ${where}.`] : []),
      ],
    };
  }

  if (job == null) {
    // NO OPEN JOB. The tap has still been recorded against the lift (or joined one just recorded), it
    // simply belongs to no job, and the engineer is told so in words that cannot be read as a logged
    // arrival. A named fresh tap is the ordinary case; every other outcome keeps its own wording.
    if (outcome === "recorded_named") {
      const unlinked = `The tap has been recorded against the lift at ${time}, but it is not linked to a job.`;
      // THREE DIFFERENT TRUTHS BEHIND ONE EMPTY ANSWER. "No open job here today" is right for the first and
      // wrong for the second, and the advice that suits an engineer does not suit the third.
      if (input.noJobReason === "all_done") {
        // Every open job here already has both ends tapped, typically a second press straight after
        // "Leaving logged". Saying there is no job would read as the platform having lost the one the
        // engineer has just finished.
        return {
          tone: "note",
          heading: `Nothing left to log at ${assetRef}`,
          lines: ["Your arrival and your leaving are both already recorded for your job here.", unlinked],
        };
      }
      if (input.noJobReason === "other_type") {
        // The engineer's open job here is a repair, technician visit or other type: the tag is for service
        // visits and call-outs, so it is logged on the job sheet. Said by type, never as "no job".
        const what = otherTypePhrase(input.otherType);
        return {
          tone: "note",
          heading: `Not logged to a job at ${assetRef}`,
          lines: [unlinked, `Your open job here is ${what}, which you log on the job sheet, not with the tag.`],
        };
      }
      if (input.noJobReason === "no_engineer") {
        // An office or admin login with no Engineer row walking the app: there is no job of their own to
        // open, so the usual advice would send them looking for something that cannot exist.
        return {
          tone: "note",
          heading: `No open job at ${assetRef}`,
          lines: [unlinked, "This account has no jobs of its own to log it against."],
        };
      }
      return {
        tone: "note",
        heading: `No open job at ${assetRef}`,
        lines: [unlinked, "If you are here for a job that is not listed, open it and tap the tag from its sheet."],
      };
    }
    return { tone: popTagOutcomeTone(outcome), heading: copy.heading, lines: [copy.body] };
  }

  // The end the server RECORDED, falling back to the end the phone asked for when the server predates
  // the field. Never the other way about: an over-claim is decided by what is on file.
  const end: PopTagEnd = result.direction === "arrival" || result.direction === "departure" ? result.direction : job.nextEnd;
  const what = candidateLabel(job);

  if (outcome === "recorded_named") {
    return { tone: "ok", heading: `${endNoun(end)} logged, ${where}`, lines: [`${time} · ${what}`] };
  }
  if (outcome === "collapsed_mine") {
    // A repeat of this job's own tap inside three minutes: the row already on file is the answer.
    return {
      tone: "ok",
      heading: `${endNoun(end)} already logged, ${where}`,
      lines: [`${time} · ${what}`, "Nothing further is needed."],
    };
  }
  if (outcome === "recorded_anonymous") {
    // The session did not reach the server, so the tap could not be linked to the job either: a job is
    // only ever attached to a tap that names its engineer.
    return {
      tone: "warn",
      heading: copy.heading,
      lines: [copy.body, `It has not been linked to your ${jobTypeLabel(job.type).toLowerCase()} either.`],
    };
  }
  return { tone: "warn", heading: copy.heading, lines: [copy.body] };
}

function popTagOutcomeTone(outcome: PopTagOutcome): HomeScanTone {
  return outcome === "collapsed_mine" ? "note" : "warn";
}

// Fold a tap that has just landed into a cached job's own record of it, so the job sheet knows before
// the next refresh and with no signal. EARLIEST arrival and LATEST departure, exactly as the server
// derives them (tapEndsByJob), so a repeat tap inside a visit changes nothing and the two never
// disagree. Returns a new object; never mutates the one it was given.
export function withTapNoted<T extends { arrivalTapAt?: string | null; departureTapAt?: string | null }>(
  job: T,
  end: PopTagEnd,
  at: string,
): T {
  const when = Date.parse(at);
  if (!Number.isFinite(when)) return job;
  if (end === "arrival") {
    const have = job.arrivalTapAt ? Date.parse(job.arrivalTapAt) : NaN;
    return Number.isFinite(have) && have <= when ? job : { ...job, arrivalTapAt: at };
  }
  const have = job.departureTapAt ? Date.parse(job.departureTapAt) : NaN;
  return Number.isFinite(have) && have >= when ? job : { ...job, departureTapAt: at };
}
